import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DUMMY_RECAPTCHA_TOKEN } from '@assetron/common/constants/constant';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { GlobalStyles } from '@mui/material';
import { useRecaptchaInterceptor } from '../hooks/useRecaptchaInterceptor'; // Import the hook
import SubscriptionLoaderWrapper from '../components/common/SubscriptionLoaderWrapper';

interface RecaptchaContextProps {
  executeRecaptcha: (action: string) => Promise<string>;
}

const RecaptchaContext = createContext<RecaptchaContextProps | undefined>(
  undefined,
);

interface RecaptchaProviderProps {
  readonly siteKey: string;
  readonly children: React.ReactNode;
}
interface RecaptchaContextWrapperProps {
  readonly children: React.ReactNode;
  readonly failOpen: boolean;
}

interface StaticRecaptchaContextWrapperProps {
  readonly children: React.ReactNode;
  readonly executeRecaptcha: (action: string) => Promise<string>;
}

const recaptchaBadgeStyles = {
  '.grecaptcha-badge': {
    display: 'none !important',
  },
} as const;

function RecaptchaContextWrapper({
  children,
  failOpen,
}: RecaptchaContextWrapperProps) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  useEffect(() => {
    if (executeRecaptcha) {
      setIsRecaptchaReady(true);
    }
  }, [executeRecaptcha]);

  const effectiveExecuteRecaptcha = useMemo(
    () =>
      executeRecaptcha ??
      (async () => {
        return DUMMY_RECAPTCHA_TOKEN;
      }),
    [executeRecaptcha],
  );

  useRecaptchaInterceptor(effectiveExecuteRecaptcha); // Use the hook here

  const contextValue = useMemo(
    () => ({ executeRecaptcha: effectiveExecuteRecaptcha }),
    [effectiveExecuteRecaptcha],
  );

  if (!isRecaptchaReady && !failOpen) {
    return <SubscriptionLoaderWrapper />; // or a loading spinner
  }

  return (
    <RecaptchaContext.Provider value={contextValue}>
      {children}
    </RecaptchaContext.Provider>
  );
}

function StaticRecaptchaContextWrapper({
  children,
  executeRecaptcha,
}: StaticRecaptchaContextWrapperProps) {
  useRecaptchaInterceptor(executeRecaptcha);

  const contextValue = useMemo(
    () => ({ executeRecaptcha }),
    [executeRecaptcha],
  );

  return (
    <RecaptchaContext.Provider value={contextValue}>
      {children}
    </RecaptchaContext.Provider>
  );
}

export function RecaptchaProvider({
  children,
  siteKey,
}: RecaptchaProviderProps) {
  const currentWindow = globalThis.window;
  const isLocalhost =
    currentWindow !== undefined &&
    (currentWindow.location.hostname === 'localhost' ||
      currentWindow.location.hostname === '127.0.0.1');
  const allowLocalFailOpen =
    isLocalhost &&
    (String(process.env.ASSETRON_RECAPTCHA_FAIL_OPEN_LOCAL).toLowerCase() ===
      'true' ||
      siteKey.trim().length === 0);
  const localFailOpenContextValue = useMemo(
    () => ({
      executeRecaptcha: async () => DUMMY_RECAPTCHA_TOKEN,
    }),
    [],
  );

  if (allowLocalFailOpen) {
    return (
      <>
        <GlobalStyles styles={recaptchaBadgeStyles} />
        <StaticRecaptchaContextWrapper
          executeRecaptcha={localFailOpenContextValue.executeRecaptcha}
        >
          {children}
        </StaticRecaptchaContextWrapper>
      </>
    );
  }

  return (
    <>
      <GlobalStyles styles={recaptchaBadgeStyles} />
      <GoogleReCaptchaProvider reCaptchaKey={siteKey} size="invisible">
        <RecaptchaContextWrapper failOpen={allowLocalFailOpen}>
          {children}
        </RecaptchaContextWrapper>
      </GoogleReCaptchaProvider>
    </>
  );
}

export const useRecaptcha = (): RecaptchaContextProps => {
  const context = useContext(RecaptchaContext);
  if (!context?.executeRecaptcha) {
    throw new Error('Recaptcha context is not available');
  }
  return context;
};
